import {memo, useEffect, useRef, useState} from "react";
import type {DragEvent} from "react";
import {useTranslation} from "react-i18next";
import {BrokerPaymentMethod} from "../../../types/Responses/BrokerPaymentMethodsResponse";

interface DraggableTableProps {
    table: BrokerPaymentMethod[];
    onChange: (order: BrokerPaymentMethod[]) => void;
}

const DraggableTable = ({table, onChange}: DraggableTableProps) => {
    const {t} = useTranslation();
    const [data, setData] = useState<BrokerPaymentMethod[]>(() => table ? [...table] : []);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const dragStartIndex = useRef<number | null>(null);
    const dragOverIndex = useRef<number | null>(null);

    useEffect(() => {
        setData(table ? [...table] : []);
    }, [table]);

    const handleDragStart = (index: number) => () => {
        dragStartIndex.current = index;
        setActiveIndex(index);
    };

    const handleDragEnter = (index: number) => (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        dragOverIndex.current = index;
    };

    const handleDragOver = (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
    };

    const resetDragState = () => {
        dragStartIndex.current = null;
        dragOverIndex.current = null;
        setActiveIndex(null);
    };

    const commitOrderChange = () => {
        const from = dragStartIndex.current;
        const to = dragOverIndex.current;

        if (from === null || to === null || from === to) {
            resetDragState();
            return;
        }

        const updated = [...data];
        const [moved] = updated.splice(from, 1);
        if (moved)
            updated.splice(to, 0, moved);

        const normalized = updated.map((item, index) => ({
            ...item,
            order: index + 1,
        }));

        setData(normalized);
        onChange(normalized);
        resetDragState();
    };

    const handleDragEnd = () => {
        commitOrderChange();
    };

    const handleDrop = (index: number) => (event: DragEvent<HTMLTableRowElement>) => {
        event.preventDefault();
        dragOverIndex.current = index;
        commitOrderChange();
    };

    return (
        <table className="draggable-table">
            <thead>
            <tr>
                <th>#</th>
                <th>{t('admin.broker_payment_methods.table.name')}</th>
            </tr>
            </thead>
            <tbody>
            {data.map((item, index) => (
                <tr
                    key={item.enabled_id ?? item.id ?? index}
                    draggable
                    onDragStart={handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDrop={handleDrop(index)}
                    className={activeIndex === index ? 'is-dragging' : ''}
                >
                    <td>{index + 1}</td>
                    <td>
                        <img
                            src={`${window.location.origin}/images/psps/${item.image}`}
                            alt={item.name}
                            className="inline-block relative object-center rounded-lg border border-blue-gray-50 dark:border-blue-gray-500 bg-white object-contain p-1 mr-2 w-16 h-8"
                        />
                        <span>{item.name}</span>
                    </td>
                </tr>
            ))}
            </tbody>
        </table>
    );
};

export default memo(DraggableTable);
